// Pure planning for the exclusion bake (荒らし復旧 第 2 段階 ①,
// vandalism-recovery-ui-2026-09-27.md §3「焼き処理 (サーバ)」).
//
// Input: the merged stroke log, the resolved document lineage, what to exclude,
// and which snapshot generations exist per lineage session. Output: the base
// generation G to start from and the exact event list to replay on top of it.
//
// Rules (all from the design doc):
//   - **G is picked automatically**: the last generation on the lineage strictly
//     before the earliest excluded event (a line baked into G cannot be removed).
//   - **Replay set** = `(G, to_i] ∪ ⋃_{j>i} [from_j, to_j]` where `i` is G's
//     document. Abandoned branches are never part of any `[from, to]`.
//   - **Strokes in flight at G**: G's PNG already holds their partial pixels, so
//     their pre-G start/segments are re-included and replayed from the start —
//     the same rule as `SessionBuffer.deltaForSnapshot(prevActiveSids)`.
//   - **Undo**: `SnapshotEngine` does not understand `stroke.cancelled`; every
//     event of a cancelled sid (tombstone included) is dropped here (pair-filter).
//     Same for a cancelled `whiteboard.region` (+ its `whiteboard.region-cancelled`).
//   - **Exclusion**: events of excluded uids and excluded sids are dropped. A
//     sid whose `stroke.start` is excluded is excluded as a whole.
const sidOf = (ev) => ev.t === 'stroke.start' ||
    ev.t === 'stroke.segment' ||
    ev.t === 'stroke.end' ||
    ev.t === 'stroke.cancelled'
    ? ev.sid
    : undefined;
/** Compact stub of a log event (no point arrays); `intern` shares string instances. */
export const toPlanEvent = (ev, intern) => {
    if (ev.t === 'whiteboard.region-cancelled') {
        return { t: ev.t, seq: ev.seq, uid: intern(ev.uid), regionSeq: ev.regionSeq };
    }
    const sid = sidOf(ev);
    return sid === undefined
        ? { t: ev.t, seq: ev.seq, uid: intern(ev.uid) }
        : { t: ev.t, seq: ev.seq, uid: intern(ev.uid), sid: intern(sid) };
};
/** Index of the document containing `seq`, or -1. Docs are ascending + disjoint. */
const docIndexOf = (docs, seq) => {
    for (let i = 0; i < docs.length; i++) {
        const d = docs[i];
        if (seq > d.eventsTo)
            continue;
        return seq >= d.eventsFrom ? i : -1;
    }
    return -1;
};
const isExcludedEvent = (ev, excludeUids, excludedSids) => {
    if (ev.t === 'whiteboard.restore')
        return false; // never apply markers; not "excluded" either
    if (excludeUids.has(ev.uid))
        return true;
    const sid = sidOf(ev);
    return sid !== undefined && excludedSids.has(sid);
};
export const planExclusionDecisions = (input) => {
    const { docs, excludeUids, generationsBySession } = input;
    // Walk the events in scope without materialising a scoped copy (the streaming bake
    // calls this with up to ~1M stubs).
    const eachScoped = (fn) => {
        let d = 0;
        for (const ev of input.events) {
            while (d < docs.length && ev.seq > docs[d].eventsTo)
                d++;
            if (d >= docs.length)
                break;
            if (ev.seq < docs[d].eventsFrom)
                continue;
            if (fn(ev, d) === false)
                return;
        }
    };
    // 1. Excluded sids = explicit ∪ strokes started by an excluded uid.
    const excludedSids = new Set(input.excludeSids);
    eachScoped((ev) => {
        if (ev.t === 'stroke.start' && excludeUids.has(ev.uid) && ev.sid !== undefined) {
            excludedSids.add(ev.sid);
        }
    });
    // 2. Earliest excluded event within the lineage drives the choice of G.
    let earliest = null;
    eachScoped((ev) => {
        if (isExcludedEvent(ev, excludeUids, excludedSids)) {
            earliest = ev.seq;
            return false; // events are seq-ascending
        }
    });
    if (earliest === null)
        return { kind: 'error', code: 'nothing_to_exclude' };
    const earliestSeq = earliest;
    // 3. G = latest (docIndex, seq) generation strictly before `earliest`.
    let chosen = null;
    for (let docIndex = 0; docIndex < docs.length; docIndex++) {
        const doc = docs[docIndex];
        for (const seq of generationsBySession.get(doc.sessionId) ?? []) {
            if (seq < doc.candidateMin || seq > doc.eventsTo || seq >= earliestSeq)
                continue;
            if (chosen === null ||
                docIndex > chosen.docIndex ||
                (docIndex === chosen.docIndex && seq > chosen.seq)) {
                chosen = { docIndex, sessionId: doc.sessionId, seq };
            }
        }
    }
    if (chosen === null) {
        const root = docs[0];
        // A blank start is only truthful when the root really starts the canvas.
        if (root === undefined || (root.origin !== 'clear' && root.origin !== 'head')) {
            return { kind: 'error', code: 'no_base_generation' };
        }
    }
    // 4. Strokes in flight at G (within G's document).
    const fromDoc = chosen === null ? 0 : chosen.docIndex;
    const baseSeq = chosen === null ? -1 : chosen.seq;
    const open = new Set();
    eachScoped((ev, docIndex) => {
        if (docIndex !== fromDoc || ev.seq > baseSeq)
            return;
        if (ev.t === 'stroke.start' && ev.sid !== undefined)
            open.add(ev.sid);
        else if (ev.t === 'stroke.end' && ev.sid !== undefined)
            open.delete(ev.sid);
    });
    // 5. Pair-filter cancelled strokes over everything we might replay.
    const cancelledSids = new Set();
    const cancelledRegionSeqs = new Set();
    let firstReincludedSeq = null;
    eachScoped((ev, docIndex) => {
        if (docIndex < fromDoc)
            return;
        const sid = sidOf(ev);
        const preBase = docIndex === fromDoc && ev.seq <= baseSeq;
        if (preBase) {
            if (sid === undefined || !open.has(sid))
                return; // not re-included
            if (firstReincludedSeq === null)
                firstReincludedSeq = ev.seq;
        }
        if (ev.t === 'stroke.cancelled' && sid !== undefined)
            cancelledSids.add(sid);
        // region の取り消しは G より後の region にしか効かない (G 以前の region は焼き込み済みで
        // server が too-old で拒否する)。tombstone が G より前にある場合は preBase で既に return 済み。
        if (ev.t === 'whiteboard.region-cancelled' && ev.regionSeq !== undefined) {
            cancelledRegionSeqs.add(ev.regionSeq);
        }
    });
    return {
        kind: 'ok',
        decisions: {
            base: chosen,
            docs,
            fromDoc,
            baseSeq,
            earliestExcludedSeq: earliestSeq,
            excludeUids,
            excludedSids,
            cancelledSids,
            cancelledRegionSeqs,
            openAtBase: open,
            firstReincludedSeq,
        },
    };
};
/** The per-event half of the plan: what happens to `ev` in the replay. */
export const classifyReplayEvent = (d, ev) => {
    const docIndex = docIndexOf(d.docs, ev.seq);
    if (docIndex < d.fromDoc)
        return 'skip'; // outside lineage or before G's document
    const sid = sidOf(ev);
    const preBase = docIndex === d.fromDoc && ev.seq <= d.baseSeq;
    if (preBase && (sid === undefined || !d.openAtBase.has(sid)))
        return 'skip';
    if (ev.t === 'whiteboard.restore')
        return 'skip'; // marker: never replayed
    if (isExcludedEvent(ev, d.excludeUids, d.excludedSids))
        return 'excluded';
    if (sid !== undefined && d.cancelledSids.has(sid))
        return 'cancelled';
    if (ev.t === 'whiteboard.region-cancelled')
        return 'cancelled';
    if (ev.t === 'whiteboard.region' && d.cancelledRegionSeqs.has(ev.seq))
        return 'cancelled';
    return preBase ? 'replay-reincluded' : 'replay';
};
/** `[from, to]` seq intervals whose events may be replayed (pre-G in-flight start included). */
export const replayScopeRanges = (d) => {
    const out = [];
    for (let i = d.fromDoc; i < d.docs.length; i++) {
        const doc = d.docs[i];
        let from = doc.eventsFrom;
        if (i === d.fromDoc) {
            from = Math.max(doc.eventsFrom, d.baseSeq + 1);
            if (d.firstReincludedSeq !== null)
                from = Math.min(from, d.firstReincludedSeq);
        }
        if (from <= doc.eventsTo)
            out.push({ fromSeq: from, toSeq: doc.eventsTo });
    }
    return out;
};
/** Eager form over full events (the original API; the streaming bake uses the two halves). */
export const planExclusion = (input) => {
    const res = planExclusionDecisions(input);
    if (res.kind === 'error')
        return res;
    const d = res.decisions;
    const out = [];
    let scopedEvents = 0;
    let droppedExcluded = 0;
    let droppedCancelled = 0;
    let reincludedKept = 0;
    for (const ev of input.events) {
        if (docIndexOf(d.docs, ev.seq) >= 0)
            scopedEvents++;
        const v = classifyReplayEvent(d, ev);
        if (v === 'excluded')
            droppedExcluded++;
        else if (v === 'cancelled')
            droppedCancelled++;
        else if (v === 'replay' || v === 'replay-reincluded') {
            if (v === 'replay-reincluded')
                reincludedKept++;
            out.push(ev);
        }
    }
    return {
        kind: 'ok',
        plan: {
            base: d.base,
            events: out,
            earliestExcludedSeq: d.earliestExcludedSeq,
            excludedSids: d.excludedSids,
            stats: {
                scopedEvents,
                droppedExcluded,
                droppedCancelled,
                reincludedInFlight: reincludedKept,
            },
        },
    };
};
//# sourceMappingURL=exclusionPlan.js.map